import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
  Button,
  Alert,
  Snackbar,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import VideocamIcon from "@mui/icons-material/Videocam";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import axios from "axios";
import config from "../../config";
import GalleryImageModal from "../../modals/GalleryImageModal";
import GalleryVideoModal from "../../modals/GalleryVideoModal";

const MAX_ITEMS = 20;

function toImgUrl(pathOrUrl, backendBase) {
  if (!pathOrUrl) return "";
  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) return pathOrUrl;
  return `${backendBase}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

function getYoutubeEmbedUrl(url = "") {
  try {
    const parsed = new URL(url);

    if (parsed.hostname.includes("youtube.com")) {
      const v = parsed.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
      const parts = parsed.pathname.split("/").filter(Boolean);
      const maybeId = parts[parts.length - 1];
      if (maybeId) return `https://www.youtube.com/embed/${maybeId}`;
    }

    if (parsed.hostname.includes("youtu.be")) {
      const id = parsed.pathname.replace("/", "");
      if (id) return `https://www.youtube.com/embed/${id}`;
    }

    return "";
  } catch {
    return "";
  }
}

function SectionCard({ title, subtitle, right, children }) {
  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={900} sx={{ color: "#fff" }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" sx={{ color: "#fff", opacity: 0.85 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {right}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function EmptyCell() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 3,
        border: "1px dashed rgba(255,255,255,0.28)",
        bgcolor: "rgba(255,255,255,0.03)",
        minHeight: 180,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Stack spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(255,255,255,0.08)",
            color: "#fff",
          }}
        >
          <AddIcon />
        </Box>

        <Typography variant="body2" sx={{ color: "#fff", opacity: 0.8, textAlign: "center" }}>
          {t("gallery.emptySlot")}
        </Typography>
      </Stack>
    </Box>
  );
}

function FilledCell({
  item,
  backendBase,
  onReplaceImage,
  onReplaceVideo,
  onDelete,
  draggableProps,
  dragHandleProps,
  isReorderMode,
}) {
  const { t } = useTranslation();
  const isImage = item?.type === "image";
  const imageSrc = isImage ? toImgUrl(item?.src, backendBase) : "";
  const embedUrl = !isImage ? getYoutubeEmbedUrl(item?.src || "") : "";

  return (
    <Box
      {...draggableProps}
      sx={{
        position: "relative",
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.16)",
        bgcolor: "rgba(255,255,255,0.04)",
        minHeight: 180,
        cursor: isReorderMode ? "grab" : "default",
        userSelect: "none",
      }}
    >
      {isImage ? (
        <Box
          sx={{
            width: "100%",
            height: 180,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={imageSrc}
            alt={item?.title || "Gallery item"}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "100%",
              display: "block",
            }}
          />
        </Box>
      ) : embedUrl ? (
        <Box sx={{ position: "relative", width: "100%", height: 180, bgcolor: "#000" }}>
          <Box
            component="iframe"
            src={embedUrl}
            title={item?.title || "YouTube video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            sx={{
              width: "100%",
              height: "100%",
              border: 0,
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              pointerEvents: "none",
              bgcolor: "rgba(0,0,0,0.12)",
            }}
          >
            <PlayCircleOutlineIcon sx={{ fontSize: 54, color: "#fff" }} />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: 180,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(0,0,0,0.35)",
          }}
        >
          <Typography sx={{ color: "#fff" }}>{t("gallery.invalidVideo")}</Typography>
        </Box>
      )}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.20) 40%, rgba(0,0,0,0.08) 100%)",
          pointerEvents: "none",
        }}
      />

      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
        }}
      >
        {isReorderMode && (
          <Tooltip title={t("gallery.moveTooltip")}>
            <IconButton
              {...dragHandleProps}
              size="small"
              sx={{
                bgcolor: "rgba(0,0,0,0.48)",
                color: "#fff",
                "&:hover": { bgcolor: "rgba(0,0,0,0.68)" },
              }}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {!isReorderMode && (
          <>
            <Tooltip title={t("gallery.replaceTooltip")}>
              <IconButton
                size="small"
                onClick={isImage ? onReplaceImage : onReplaceVideo}
                sx={{
                  bgcolor: "rgba(0,0,0,0.48)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.68)" },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("gallery.deleteTooltip")}>
              <IconButton
                size="small"
                onClick={onDelete}
                sx={{
                  bgcolor: "rgba(0,0,0,0.48)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.68)" },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>

      <Box
        sx={{
          position: "absolute",
          left: 10,
          right: 10,
          bottom: 10,
          zIndex: 2,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "#fff",
            display: "block",
            fontWeight: 700,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item?.title || (isImage ? t("gallery.image") : t("gallery.video"))}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255,255,255,0.78)",
            display: "block",
          }}
        >
          {isImage ? "Image" : "Video"}
        </Typography>
      </Box>
    </Box>
  );
}

export default function ClientGalleryPage() {
  const { t } = useTranslation();

  const token = useMemo(() => localStorage.getItem("admin_jwt"), []);
  const backendBase = config.BACKEND_BASE_URL;

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [draftItems, setDraftItems] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);

  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  const [editingItem, setEditingItem] = useState(null);
  const [draggedId, setDraggedId] = useState(null);

  const notify = (type, msg) => setToast({ open: true, type, msg });

  const loadGallery = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${config.BACKEND_BASE_URL}/api/admin/images/client/gallery`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = Array.isArray(res.data?.gallery)
        ? res.data.gallery
        : Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
            ? res.data
            : [];

      const normalized = data.slice(0, MAX_ITEMS);

      setItems(normalized);
      setDraftItems(normalized);
      setHasOrderChanges(false);
      setIsReorderMode(false);
    } catch (err) {
      console.error("Load gallery failed:", err);
      notify("error", t("gallery.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGallery();
  }, []);

  const visibleItems = isReorderMode ? draftItems : items;
  const emptyCount = Math.max(0, MAX_ITEMS - visibleItems.length);
  const gridCells = [
    ...visibleItems,
    ...Array.from({ length: emptyCount }, (_, i) => ({ _empty: true, key: `empty-${i}` })),
  ];

  const openAddImage = () => {
    if (items.length >= MAX_ITEMS) {
      notify("error", t("gallery.maxReached"));
      return;
    }
    setEditingItem(null);
    setImageModalOpen(true);
  };

  const openAddVideo = () => {
    if (items.length >= MAX_ITEMS) {
      notify("error", t("gallery.maxReached"));
      return;
    }
    setEditingItem(null);
    setVideoModalOpen(true);
  };

  const openEditImage = (item) => {
    setEditingItem(item);
    setImageModalOpen(true);
  };

  const openEditVideo = (item) => {
    setEditingItem(item);
    setVideoModalOpen(true);
  };

  const handleDelete = async (item) => {
    try {
      await axios.delete(`${config.BACKEND_BASE_URL}/api/admin/images/client/gallery/${item._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      notify("success", t("gallery.deleted"));
      await loadGallery();
    } catch (err) {
      console.error("Delete gallery item failed:", err);
      notify("error", t("gallery.deleteFailed"));
    }
  };

  const handleEnableReorder = () => {
    setDraftItems(items);
    setIsReorderMode(true);
    setHasOrderChanges(false);
  };

  const handleCancelReorder = () => {
    setDraftItems(items);
    setIsReorderMode(false);
    setHasOrderChanges(false);
    setDraggedId(null);
  };

  const handleSaveReorder = async () => {
    try {
      setSavingOrder(true);

      await axios.patch(`${config.BACKEND_BASE_URL}/api/admin/images/client/gallery/reorder`, {
        itemIds: draftItems.map((x) => x._id),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      notify("success", t("gallery.orderSaved"));
      await loadGallery();
    } catch (err) {
      console.error("Reorder gallery failed:", err);
      notify("error", t("gallery.orderFailed"));
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDragStart = (id) => {
    if (!isReorderMode) return;
    setDraggedId(id);
  };

  const handleDropOn = (targetId) => {
    if (!isReorderMode) return;
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      return;
    }

    const current = [...draftItems];
    const fromIndex = current.findIndex((x) => x._id === draggedId);
    const toIndex = current.findIndex((x) => x._id === targetId);

    if (fromIndex === -1 || toIndex === -1) {
      setDraggedId(null);
      return;
    }

    const next = [...current];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);

    setDraftItems(next);
    setDraggedId(null);

    const changed =
      JSON.stringify(next.map((x) => x._id)) !== JSON.stringify(items.map((x) => x._id));

    setHasOrderChanges(changed);
  };

  const handleImageSaved = async () => {
    await loadGallery();
  };

  const handleVideoSaved = async () => {
    await loadGallery();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: "grid", placeItems: "center", minHeight: "65vh" }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      <SectionCard
        title={t("gallery.title")}
        subtitle={t("gallery.subtitle")}
        right={
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: { xs: 1, md: 0 } }}>
            {!isReorderMode ? (
              <>
                <Button
                  variant="contained"
                  startIcon={<PhotoCameraIcon />}
                  onClick={openAddImage}
                  disabled={items.length >= MAX_ITEMS}
                >
                  {t("gallery.newImage")}
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<VideocamIcon />}
                  onClick={openAddVideo}
                  disabled={items.length >= MAX_ITEMS}
                  sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.35)" }}
                >
                  {t("gallery.newVideo")}
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<DragIndicatorIcon />}
                  onClick={handleEnableReorder}
                  disabled={items.length < 2}
                  sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.35)" }}
                >
                  {t("gallery.reorder")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={handleCancelReorder}
                  disabled={savingOrder}
                  sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.35)" }}
                >
                  {t("gallery.cancelReorder")}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleSaveReorder}
                  disabled={!hasOrderChanges || savingOrder}
                >
                  {savingOrder ? t("gallery.savingOrder") : t("gallery.saveOrder")}
                </Button>
              </>
            )}
          </Box>
        }
      >
        {isReorderMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("gallery.reorderHint")}
          </Alert>
        )}

        <Typography variant="body2" sx={{ color: "#fff", opacity: 0.82, mb: 2 }}>
          {t("gallery.itemCount", { count: items.length, max: MAX_ITEMS })}
        </Typography>

        <Grid container spacing={2}>
          {gridCells.map((cell, index) => (
            <Grid item xs={12} sm={6} md={3} key={cell._id || cell.key || index}>
              {cell._empty ? (
                <EmptyCell />
              ) : (
                <FilledCell
                  item={cell}
                  backendBase={backendBase}
                  onReplaceImage={() => openEditImage(cell)}
                  onReplaceVideo={() => openEditVideo(cell)}
                  onDelete={() => handleDelete(cell)}
                  isReorderMode={isReorderMode}
                  draggableProps={{
                    draggable: isReorderMode,
                    onDragStart: () => handleDragStart(cell._id),
                    onDragOver: (e) => isReorderMode && e.preventDefault(),
                    onDrop: () => handleDropOn(cell._id),
                  }}
                  dragHandleProps={{
                    onMouseDown: (e) => e.stopPropagation(),
                    onTouchStart: (e) => e.stopPropagation(),
                  }}
                />
              )}
            </Grid>
          ))}
        </Grid>
      </SectionCard>

      <GalleryImageModal
        open={imageModalOpen}
        onClose={() => {
          setImageModalOpen(false);
          setEditingItem(null);
        }}
        mode={editingItem ? "edit" : "create"}
        item={editingItem}
        notify={notify}
        onSaved={handleImageSaved}
        token={token}
        backendBase={backendBase}
      />

      <GalleryVideoModal
        open={videoModalOpen}
        onClose={() => {
          setVideoModalOpen(false);
          setEditingItem(null);
        }}
        mode={editingItem ? "edit" : "create"}
        item={editingItem}
        notify={notify}
        onSaved={handleVideoSaved}
        token={token}
        backendBase={backendBase}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      >
        <Alert
          onClose={() => setToast((p) => ({ ...p, open: false }))}
          severity={toast.type}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
    </DashboardLayout>
  );
}